/**
 * 字段元数据注册表（单一事实来源）
 *
 * 替代散落在 5+ 个文件中的字段分类定义：
 * - editorHelpers.ts 的 23 个手写枚举转换函数
 * - cellPipeline.ts 的 ENUM_FIELDS / ENUM_VALID_LABELS
 * - pasteHelpers.ts 的 RELATION_FIELDS / RELATION_DB_FIELD / MISC_FEE_DESC_FIELDS
 * - index.tsx 的 getClearValue / RELATION_DB_FIELD
 * - registerEditors.ts 的 8 个重复注册
 *
 * 所有消费方从此注册表派生，新增/修改字段只需改这一处。
 */

import type { SailScheduleModel, PartnerModel, CarModel, LockedInfo, LockEntry } from '@ay-shared-core/types/wuliu'
import {
  WaybillTosEnum,
  WaybillTosText,
  WaybillTypeEnum,
  WaybillTypeText,
  FhStatusEnum,
  FhStatusText,
  // RushStatusEnum, // 隐藏字段：加急
  // RushStatusText,
  // ReturnBoxStatusEnum, // 隐藏字段：还柜
  // ReturnBoxStatusText,
  ShipReceiptStatusEnum,
  ShipReceiptStatusText,
  PartnerReceiptStatusEnum,
  PartnerReceiptStatusText,
  BoxConditionEnum,
  BoxConditionText,
  BillStatusEnum,
} from '@ay-shared-core/types/wuliu'
import type { RelationCache } from '../hooks/useWaybillReducer'

// ==================== 字段定义类型 ====================

export interface EnumFieldDef {
  kind: 'enum'
  /** 编辑器注册名（如 'tos-editor'） */
  editorName: string
  /** 值→显示文本映射（来自 shared types） */
  textMap: Record<number, string>
  /** 默认值（null/无效输入的回退值） */
  defaultValue: number
}

export interface RelationFieldDef {
  kind: 'relation'
  /** 数据库外键字段名（如 'sail_schedule_id'） */
  dbField: string
}

export interface MoneyFieldDef {
  kind: 'money'
}

export interface DateTimeFieldDef {
  kind: 'datetime'
}

export interface MiscFeeDescFieldDef {
  kind: 'misc_fee_desc'
  /** 对应的 _fee 总和字段 */
  totalField: string
}

export interface TextFieldDef {
  kind: 'text'
}

export interface NonClearableFieldDef {
  kind: 'non_clearable'
}

export type FieldDef =
  | EnumFieldDef
  | RelationFieldDef
  | MoneyFieldDef
  | DateTimeFieldDef
  | MiscFeeDescFieldDef
  | TextFieldDef
  | NonClearableFieldDef

// ==================== 注册表 ====================

export const FIELD_REGISTRY: Record<string, FieldDef> = {
  // 枚举字段
  tos: { kind: 'enum', editorName: 'tos-editor', textMap: WaybillTosText, defaultValue: WaybillTosEnum.Unset },
  type: { kind: 'enum', editorName: 'type-editor', textMap: WaybillTypeText, defaultValue: WaybillTypeEnum.Unset },
  fh_status: { kind: 'enum', editorName: 'fh-status-editor', textMap: FhStatusText, defaultValue: FhStatusEnum.Unreleased },
  ship_receipt_status: { kind: 'enum', editorName: 'ship-receipt-status-editor', textMap: ShipReceiptStatusText, defaultValue: ShipReceiptStatusEnum.NoNeed },
  partner_receipt_status: { kind: 'enum', editorName: 'partner-receipt-status-editor', textMap: PartnerReceiptStatusText, defaultValue: PartnerReceiptStatusEnum.NoNeed },
  box_condition: { kind: 'enum', editorName: 'box-condition-editor', textMap: BoxConditionText, defaultValue: BoxConditionEnum.NoNeed },
  // 隐藏字段：加急状态（后端+DB保留，前端暂时隐藏）
  // rush_status: { kind: 'enum', editorName: 'rush-status-editor', textMap: RushStatusText, defaultValue: RushStatusEnum.No },
  // 隐藏字段：还柜状态（后端+DB保留，前端暂时隐藏）
  // return_box_status: { kind: 'enum', editorName: 'return-box-status-editor', textMap: ReturnBoxStatusText, defaultValue: ReturnBoxStatusEnum.No },

  // 关联字段
  sail_schedule: { kind: 'relation', dbField: 'sail_schedule_id' },
  partner_id: { kind: 'relation', dbField: 'partner_id' },
  car: { kind: 'relation', dbField: 'car_id' },

  // 金额字段
  ship_company_towing_fee: { kind: 'money' },
  partner_towing_fee: { kind: 'money' },
  car_fee: { kind: 'money' },

  // 日期时间字段
  car_finished_date: { kind: 'datetime' },

  // 杂费说明字段
  // 隐藏字段：船公司杂费说明（后端+DB保留，前端暂时隐藏）
  // ship_company_towing_fee_desc: { kind: 'misc_fee_desc', totalField: 'ship_company_other_fee' },
  partner_other_fee_desc: { kind: 'misc_fee_desc', totalField: 'partner_other_fee' },
  car_other_fee_desc: { kind: 'misc_fee_desc', totalField: 'car_other_fee' },

  // 不可清空字段
  box: { kind: 'non_clearable' },
}

// ==================== 派生集合 ====================

/** 所有枚举字段名 */
export const ENUM_FIELDS = new Set(
  Object.entries(FIELD_REGISTRY).filter(([, d]) => d.kind === 'enum').map(([k]) => k)
)

/** 枚举字段 → 合法文本集合（粘贴校验 + 编辑器标签检测） */
export const ENUM_VALID_LABELS: Record<string, Set<string>> = Object.fromEntries(
  Object.entries(FIELD_REGISTRY)
    .filter((e): e is [string, EnumFieldDef] => e[1].kind === 'enum')
    .map(([k, d]) => [k, new Set(Object.values(d.textMap))])
)

/** 关联字段（通过弹窗绑定或粘贴反查） */
export const RELATION_FIELDS = new Set(
  Object.entries(FIELD_REGISTRY).filter(([, d]) => d.kind === 'relation').map(([k]) => k)
)

/**
 * VTable 列名 → DB 字段名映射(关联字段:`car` 列 → `car_id` 字段)
 *
 * 用途:
 *   1. 锁查询时把 VTable 列 field 映射到后端 LockEntry.field 用的 DB 字段名
 *   2. handleBatchClear 清空关联列时取 DB 字段名(关联列在 DB 是 id 字段)
 *
 * 注:本表是 UI 列名 ↔ DB 列名的"显示层适配",不是业务规则映射。
 * 业务规则(账单 → 字段组)在后端 billFieldLockMap 单一权威源。
 */
export const RELATION_DB_FIELD: Record<string, string> = Object.fromEntries(
  Object.entries(FIELD_REGISTRY)
    .filter((e): e is [string, RelationFieldDef] => e[1].kind === 'relation')
    .map(([k, d]) => [k, d.dbField])
)

/**
 * 判断字段是否被锁定(字段锁规则见 docs/02-海运单需求.md WB-011~014)。
 *
 * fieldName 接受 VTable 列 field('car' / 'sail_schedule' 等)或直接 DB 字段名,
 * 通过 RELATION_DB_FIELD 映射到 DB 字段名后再查 locks 清单。
 *
 * 仅 Confirmed/Paid 状态构成"锁"(不可编辑);Default 状态是绑定关系但仍可编辑 —
 * 拆分两层语义,见 docs/02 WB-011/012 + ComputeWaybillLockedInfo 注释。
 */
export function isFieldLocked(info: LockedInfo | null | undefined, fieldName: string): boolean {
  if (!info) return false
  const lockKey = RELATION_DB_FIELD[fieldName] ?? fieldName
  return info.locks.some((l) => l.field === lockKey && l.bill_status !== BillStatusEnum.Default)
}

/**
 * 查找指定字段的锁源(hover Tooltip 显示「被《xxx》锁定」+ 跳转账单)。
 *
 * 返 null = 该字段未锁;非 null = 锁该字段的账单详情。
 */
export function findLockSource(info: LockedInfo | null | undefined, fieldName: string): LockEntry | null {
  if (!info || info.locks.length === 0) return null
  const lockKey = RELATION_DB_FIELD[fieldName] ?? fieldName
  return info.locks.find((l) => l.field === lockKey) ?? null
}

/** 金额字段（编辑时需剥离千分位逗号） */
export const MONEY_FIELDS = new Set(
  Object.entries(FIELD_REGISTRY).filter(([, d]) => d.kind === 'money').map(([k]) => k)
)

/** 日期时间字段 */
export const DATETIME_FIELDS = new Set(
  Object.entries(FIELD_REGISTRY).filter(([, d]) => d.kind === 'datetime').map(([k]) => k)
)

/** 杂费说明字段 */
export const MISC_FEE_DESC_FIELDS = new Set(
  Object.entries(FIELD_REGISTRY).filter(([, d]) => d.kind === 'misc_fee_desc').map(([k]) => k)
)

/**
 * 基于字段类型决定清空值
 *
 * @param field 数据库字段名（关联字段传 dbField 如 'sail_schedule_id'）
 * @returns 清空值，undefined 表示该字段不允许清空
 */
export const getClearValue = (field: string): unknown => {
  // 关联字段的 dbField（如 sail_schedule_id）不在 FIELD_REGISTRY 中，需反查
  const isRelationDbField = Object.values(FIELD_REGISTRY).some(
    (d) => d.kind === 'relation' && d.dbField === field
  )
  if (isRelationDbField) return null

  const def = FIELD_REGISTRY[field]
  if (!def) return ''  // 未注册的字段视为纯文本 → 空字符串

  switch (def.kind) {
    case 'enum':          return def.defaultValue  // 枚举 → 默认值（0）
    case 'relation':      return null              // 关联显示字段 → null
    case 'money':         return ''                // 金额 → 空字符串（string 存储）
    case 'datetime':      return null              // 日期 → null（数据库指针字段允许 NULL）
    case 'misc_fee_desc': return ''                // 杂费说明 → 空字符串
    case 'text':          return ''                // 文本 → 空字符串
    case 'non_clearable': return undefined         // 不可清空
  }
}

// ==================== 预构建枚举反向映射（文本→值） ====================

const enumReverseMaps: Record<string, Map<string, number>> = {}
for (const [field, def] of Object.entries(FIELD_REGISTRY)) {
  if (def.kind !== 'enum') continue
  const reverse = new Map<string, number>()
  for (const [numStr, text] of Object.entries(def.textMap)) {
    reverse.set(text, Number(numStr))
  }
  enumReverseMaps[field] = reverse
}

// ==================== 通用枚举转换函数 ====================

/** 枚举文本 → 数据库值（无效输入返回 defaultValue） */
export const enumTextToValue = (field: string, text: string): number => {
  const def = FIELD_REGISTRY[field]
  if (def?.kind !== 'enum') return 0
  return enumReverseMaps[field]?.get(text) ?? def.defaultValue
}

/** 数据库值 → 显示文本（null/无效回退到 defaultValue 的文本） */
export const enumValueToText = (field: string, val: number | null): string => {
  const def = FIELD_REGISTRY[field]
  if (def?.kind !== 'enum') return ''
  if (val === null || val === undefined) return def.textMap[def.defaultValue] ?? ''
  return def.textMap[val] ?? def.textMap[def.defaultValue] ?? ''
}

/** 判断值是否是该枚举字段的合法显示文本 */
export const isEnumLabel = (field: string, value: unknown): boolean => {
  const labels = ENUM_VALID_LABELS[field]
  return !!labels && typeof value === 'string' && labels.has(value)
}

/** 获取枚举字段的选项列表（编辑器下拉框用） */
export const getEnumLabels = (field: string): string[] => {
  const def = FIELD_REGISTRY[field]
  if (def?.kind !== 'enum') return []
  return Object.values(def.textMap)
}

// ==================== 关联字段反查（粘贴用） ====================

/** 格式化船期为显示文本（与 constants.ts fieldFormat 保持一致） */
export const formatSailScheduleText = (s: SailScheduleModel | null | undefined): string => {
  if (!s) return ''
  const parts: string[] = []
  if (s.ship_company?.name) parts.push(`(${s.ship_company.name})`)
  if (s.arrival_date) {
    // 纯字符串提取 MM-DD（禁止 new Date 解析，避免时区偏移）
    const match = String(s.arrival_date).match(/(\d{4})-(\d{2})-(\d{2})/)
    if (match) parts.push(`${match[2]}-${match[3]}`)
  }
  parts.push(s.voyage ? `${s.name}/${s.voyage}` : s.name)
  return parts.join(' ')
}

export interface RelationResolveResult {
  id: number
  dbField: string
  obj: SailScheduleModel | PartnerModel | CarModel
}

/**
 * 从显示文本反查关联对象（粘贴时使用）
 *
 * @returns 匹配结果，null 表示未找到或有歧义（多个同名匹配）
 */
export const resolveRelationText = (
  field: string,
  text: string,
  cache: RelationCache,
): RelationResolveResult | null => {
  const def = FIELD_REGISTRY[field]
  if (def?.kind !== 'relation') return null
  const trimmed = text.trim()
  if (trimmed === '') return null

  if (field === 'sail_schedule') {
    const match = cache.sailSchedules.find((s) => formatSailScheduleText(s) === trimmed)
    if (!match) return null
    return { id: match.id, dbField: def.dbField, obj: match }
  }

  if (field === 'partner_id') {
    const matches = cache.partners.filter((p) => p.name === trimmed)
    if (matches.length !== 1) return null // 0 个或多个同名 → 拒绝
    return { id: matches[0].id, dbField: def.dbField, obj: matches[0] }
  }

  if (field === 'car') {
    const match = cache.cars.find((c) => c.number === trimmed)
    if (!match) return null
    return { id: match.id, dbField: def.dbField, obj: match }
  }

  return null
}
